/* Intelligems -> Clarity split-test bridge.

   Stamps every Intelligems experiment assignment on this device into
   Microsoft Clarity as session custom tags, so any test - current or
   future - can be segmented in Clarity (Filters, then Custom tags) with
   no per-test wiring.

   Source of truth: localStorage['ig-vars'], Intelligems' own assignment
   store, shaped { <experiment-id-last-12>: <variation-id-last-12> }. It
   is written by the Intelligems module when it assigns (2.5-5s in for a
   new visitor) and is already present at load for returning visitors.
   If Intelligems ever renames the key the bridge goes quiet; nothing
   else breaks. Entries for ENDED tests linger in returning visitors'
   storage, so their ig_* tags keep appearing in Clarity - ignore those
   keys when analysing.

   Clarity is NOT loaded by the theme directly: it arrives through the
   deferred third-party stack (lazyload2 boots on first interaction or a
   4s floor), so window.clarity does not exist when this script first
   runs. The Clarity-side dedup therefore only latches when the call was
   actually deliverable - an assignment seen while window.clarity is
   still absent is retried on every sweep until Clarity lands or the
   poll caps out. The dataLayer copy has its own dedup because GTM
   replays its queue and needs no waiting.

   Two tag families per assignment:
   - ig_<experiment-suffix> = <variation-suffix>  (generic, every test)
   - a named tag from NAMES for tests that want readable arm values.
     The per-test expose*Arm functions in theme.liquid stamp
     bundle/delta/deal/labor at the moment their arm RENDERS, which is
     the more truthful signal - NAMES only fills the gaps (GAH-HERO-01
     shipped with no exposure hook at all).

   Clarity accepts custom tags at any point in the session and applies
   them to the whole session, so late stamping is safe and cannot bias
   an arm. Do NOT hook window.onIgReady here - that single slot belongs
   to global.js for cart attribution. */
(() => {
  if (window.GD_CLARITY_BRIDGE) return;

  const NAMES = {
    // GAH-HERO-01 - still hero vs the autoplay loop (7b9ddecb-...)
    '79044885a81e': { tag: 'gah_hero_arm', arms: { d3c6749e9ab1: 'a_still', bf54a486082c: 'b_video' } },
  };

  // Object.create(null): a '__proto__' key from a corrupt ig-vars would
  // hit the prototype setter on a plain {} and silently defeat the dedup
  const clarityStamped = Object.create(null);
  const layerStamped = Object.create(null);

  const setTag = (key, value) => {
    try {
      window.clarity('set', key, value);
    } catch (e) {
      /* ignore */
    }
  };

  const pushLayer = (experiment, variation) => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'gd_ig_arm', gd_ig_experiment: experiment, gd_ig_variation: variation });
    } catch (e) {
      /* ignore */
    }
  };

  // storage throws in private-mode Safari; a missing read means no
  // stamps, never a broken page. Non-object JSON (a bare string would
  // make Object.keys walk its characters) reads as empty.
  const readAssignments = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem('ig-vars') || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
      return {};
    }
  };

  const sweep = () => {
    const vars = readAssignments();
    Object.keys(vars).forEach((experiment) => {
      const variation = vars[experiment];
      if (typeof variation !== 'string' || !variation) return;
      if (variation === '_PERMA_EXCLUDED' || experiment === 'redirectedFrom') return;

      if (layerStamped[experiment] !== variation) {
        layerStamped[experiment] = variation;
        pushLayer(experiment, variation);
      }

      // only latch once the call is deliverable - see the header
      if (!window.clarity) return;
      if (clarityStamped[experiment] === variation) return;
      clarityStamped[experiment] = variation;
      setTag('ig_' + experiment, variation);
      if (Object.prototype.hasOwnProperty.call(NAMES, experiment)) {
        const named = NAMES[experiment];
        const arm = named.arms ? named.arms[variation] : undefined;
        setTag(named.tag, typeof arm === 'string' ? arm : variation);
      }
    });
  };

  /* Returning visitors already carry ig-vars, so the first sweep can
     stamp as soon as Clarity exists. A new visitor gets assignments
     only once the async Intelligems module lands (2.5-5s), and Clarity
     itself boots on interaction or the 4s lazyload floor, so the cap
     leaves both plenty of room. */
  const STEP_MS = 500;
  const CAP_MS = 20000;
  let waited = 0;
  const tick = () => {
    sweep();
    waited += STEP_MS;
    if (waited >= CAP_MS) return;
    window.setTimeout(tick, STEP_MS);
  };
  tick();

  window.GD_CLARITY_BRIDGE = { stamped: clarityStamped, sweep };
})();
